import React from 'react';
import * as Sentry from '@sentry/react';

/**
 * Global error boundary — catches runtime errors anywhere in the React tree and
 * renders a fallback UI instead of a blank white screen.
 *
 * iter147 — Special handling for Chrome/Edge auto-translate crashes. When the
 * browser translates the page, it rewrites text DOM nodes which collides with
 * React's reconciliation and throws:
 *
 *     Failed to execute 'removeChild' on 'Node':
 *     The node to be removed is not a child of this node.
 *
 * (same family: ``insertBefore``, ``appendChild`` on a wrong parent).
 *
 * These errors are RECOVERABLE — the underlying React state is fine, the DOM
 * is just transiently out-of-sync. We auto-retry once silently before showing
 * the scary fallback so most users never see anything. If it crashes AGAIN
 * after the auto-retry, we show the fallback with a translate-specific tip.
 */

// Regex catches the three flavours of translate-DOM clashes.
const TRANSLATE_DOM_ERR = /(removeChild|insertBefore|appendChild).*(not a child|Failed to execute)/i;

function isTranslateDomError(err) {
  if (!err) return false;
  const msg = (err.message || err.toString() || '');
  return TRANSLATE_DOM_ERR.test(msg);
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    // autoRecoveredOnce — true once we've already silently auto-recovered, so
    // a second consecutive crash falls through to the visible fallback.
    this.state = { hasError: false, error: null, autoRecoveredOnce: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('ErrorBoundary caught:', error, info?.componentStack);
    // Auto-recover ONCE on a translate-induced DOM error. Use a microtask so we
    // unmount the broken tree first, then re-render fresh on the next tick.
    if (isTranslateDomError(error) && !this.state.autoRecoveredOnce) {
      Promise.resolve().then(() => {
        this.setState({ hasError: false, error: null, autoRecoveredOnce: true });
      });
      return; // Silent auto-recovery — not worth reporting to Sentry
    }
    // Report genuine UI crashes to Sentry (no PII: no user fields, no props).
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info?.componentStack || '' } },
      tags: { component: 'error-boundary', is_translate_error: String(isTranslateDomError(error)) },
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleHardReload = () => {
    // Clear caches and hard-reload. Fixes stale-build issues after deployment.
    if ('caches' in window) {
      caches.keys().then(keys => Promise.all(keys.map(k => caches.delete(k))))
        .finally(() => window.location.reload(true));
    } else {
      window.location.reload(true);
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = this.state.error?.message || 'Something went wrong.';
    const isTranslate = isTranslateDomError(this.state.error);
    return (
      <div translate="no" className="notranslate min-h-screen bg-[#FAF7F5] flex items-center justify-center p-6" data-testid="error-boundary">
        <div className="max-w-md w-full bg-white rounded-2xl border border-stone-200 shadow-lg p-6 text-center">
          <div className="w-14 h-14 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4 text-3xl">⚠</div>
          <h2 className="text-lg font-semibold text-stone-800 mb-1">Something didn't load correctly</h2>
          <p className="text-xs text-stone-500 mb-4">
            {isTranslate
              ? 'Your browser tried to translate this page, which interferes with the app. Please turn OFF "Translate" / "Auto-translate" in your browser for this site and reload.'
              : 'The page hit an unexpected error. This is almost always a stale cache after an update.'}
          </p>
          <details className="text-[10px] text-stone-400 mb-4 text-left bg-stone-50 rounded px-2 py-1.5 border border-stone-200">
            <summary className="cursor-pointer">Technical detail</summary>
            <code className="block mt-1 whitespace-pre-wrap break-words">{msg}</code>
          </details>
          <div className="flex gap-2">
            <button onClick={this.handleRetry} className="flex-1 px-4 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg text-sm font-medium transition-colors" data-testid="eb-retry">
              Try again
            </button>
            <button onClick={this.handleHardReload} className="flex-1 px-4 py-2 bg-[#6B4D5F] hover:bg-[#5A3D4E] text-white rounded-lg text-sm font-medium transition-colors" data-testid="eb-hard-reload">
              Clear cache & reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
